import React, { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "pwa-install-dismissed-at";
const DISMISS_DAYS = 7;

function wasRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    const ageDays = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_DAYS;
  } catch { return false; }
}

function isStandalone(): boolean {
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  if ((window.navigator as any).standalone === true) return true;
  return false;
}

function isIos(): boolean {
  const ua = window.navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installed = () => { setVisible(false); setDeferredPrompt(null); };
    window.addEventListener("appinstalled", installed);

    if (isIos() && !isStandalone()) {
      setTimeout(() => setVisible(true), 4000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") { setVisible(false); setDeferredPrompt(null); }
      return;
    }
    if (isIos()) setShowIosHint(true);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-40 bg-white border border-gray-200 rounded-2xl shadow-2xl p-4 animate-in fade-in slide-in-from-bottom-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
            <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
              <circle cx="18" cy="18" r="14" stroke="#60A5FA" strokeWidth="2.5" opacity="0.4"/>
              <circle cx="18" cy="18" r="8" stroke="#60A5FA" strokeWidth="2.5" opacity="0.7"/>
              <circle cx="18" cy="18" r="3" fill="#60A5FA"/>
              <line x1="18" y1="0" x2="18" y2="7" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round"/>
              <line x1="18" y1="29" x2="18" y2="36" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round"/>
              <line x1="0" y1="18" x2="7" y2="18" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round"/>
              <line x1="29" y1="18" x2="36" y2="18" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900 text-sm">Install TrackPoint</div>
            <div className="text-xs text-gray-500 mt-0.5">Add to your home screen for one-tap access.</div>
            <div className="flex items-center gap-2 mt-3">
              <button onClick={install}
                className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-700">
                <Download size={14}/> Install
              </button>
              <button onClick={dismiss}
                className="text-gray-500 hover:text-gray-800 px-2 py-1.5 text-xs">Not now</button>
            </div>
          </div>
          <button onClick={dismiss} className="text-gray-400 hover:text-gray-600 shrink-0" aria-label="Close">
            <X size={16}/>
          </button>
        </div>
      </div>

      {showIosHint && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setShowIosHint(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b bg-blue-50 flex items-center justify-between">
              <div className="text-lg font-semibold text-blue-800">Install on iPhone/iPad</div>
              <button onClick={() => setShowIosHint(false)} className="text-gray-500 hover:text-gray-800"><X size={20}/></button>
            </div>
            <div className="p-5 space-y-4 text-sm text-gray-700">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center shrink-0">1</div>
                <div>Tap the <strong>Share</strong> button <Share size={14} className="inline text-blue-600 mx-1"/> in Safari's bottom bar.</div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center shrink-0">2</div>
                <div>Scroll and tap <strong>Add to Home Screen</strong> <Plus size={14} className="inline text-blue-600 mx-1"/></div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center shrink-0">3</div>
                <div>Tap <strong>Add</strong> — the TrackPoint icon will appear on your home screen.</div>
              </div>
              <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                💡 On iOS, only <strong>Safari</strong> can install web apps (not Chrome/Firefox).
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
